import { createFileRoute, Outlet, redirect, useRouter, useRouterState } from "@tanstack/react-router";

import { ActivationRecoveryPanel } from "@/components/auth/activation-recovery-panel";
import { SidebarProvider } from "@/components/ui/sidebar";

import {
  getDashboardOperatorAccessServerFn,
  getDashboardActivationStateServerFn,
  getDashboardActivationNoticeServerFn,
  getDashboardSidebarServerFn,
  setDashboardSidebarServerFn,
} from "./-components/functions";
import { DashboardSidebar } from "./-components/sidebar";

export const Route = createFileRoute("/dashboard")({
  component: RouteComponent,
  beforeLoad: async ({ context }) => {
    if (!context.session) throw redirect({ to: "/auth/login", replace: true });
    return { session: context.session };
  },
  loader: async ({ location }) => {
    const search = new URLSearchParams(location.searchStr);
    const activationTrigger = search.has("activation");
    const verificationExpired = search.get("error") === "TOKEN_EXPIRED";
    const [sidebarState, operatorAccess, activationState, activationNotice] = await Promise.all([
      getDashboardSidebarServerFn(),
      getDashboardOperatorAccessServerFn(),
      getDashboardActivationStateServerFn(),
      getDashboardActivationNoticeServerFn({ data: { trigger: activationTrigger, verificationExpired } }),
    ]);
    return { sidebarState, operatorAccess, activationState, activationNotice };
  },
});

function RouteComponent() {
  const router = useRouter();
  const location = useRouterState({ select: (state) => state.location });
  const { session } = Route.useRouteContext();
  const { sidebarState, operatorAccess, activationState, activationNotice } = Route.useLoaderData();
  // The verification callback may include an activation query value as a
  // navigation hint, but URL text is never authority for activation, quota, or
  // grant-recovery claims.  The panel derives the durable access boundary from
  // the authenticated server session; success/recovery notices require a
  // server-owned record before they are introduced here.

  const handleSidebarOpenChange = async (open: boolean) => {
    await setDashboardSidebarServerFn({ data: open });
    void router.invalidate();
  };

  return (
    <SidebarProvider open={sidebarState} onOpenChange={handleSidebarOpenChange}>
      <DashboardSidebar operatorAccess={operatorAccess} />

      <main className="@container flex-1 p-4 md:ps-2">
        <ActivationRecoveryPanel
          activationState={
            activationState ?? (session.user.banned ? "disabled" : session.user.emailVerified ? "active" : "registered")
          }
          safeReturnTarget={`${location.pathname}${location.hash}`}
          email={session.user.email}
          notice={activationNotice}
        />
        <Outlet />
      </main>
    </SidebarProvider>
  );
}
